import { useEffect, useMemo, useState } from "react";

import { configuredRealtimeWsUrl, parseRealtimeMarketMessage, type RealtimeMarketMessage, type RealtimeMarketStatus } from "../lib/realtimeMarketData";

interface RealtimeMarketState {
  status: RealtimeMarketStatus;
  symbols: RealtimeMarketMessage["symbols"] | null;
  lastMessageAt: number | null;
  error: string | null;
}

const STALE_AFTER_MS = 5_000;

function authToken(): string | null {
  try {
    return localStorage.getItem("qst_token");
  } catch {
    return null;
  }
}

export function useRealtimeMarketFeatures(session: "asia" | "us" | "all") {
  const wsUrl = useMemo(
    () => configuredRealtimeWsUrl(import.meta.env.VITE_REALTIME_MARKET_WS_URL),
    [],
  );
  const [state, setState] = useState<RealtimeMarketState>({
    status: wsUrl ? "connecting" : "disabled",
    symbols: null,
    lastMessageAt: null,
    error: null,
  });

  useEffect(() => {
    if (!wsUrl) return;

    let closed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let staleTimer: ReturnType<typeof setInterval> | null = null;

    const connect = () => {
      if (closed) return;
      setState(prev => ({ ...prev, status: prev.symbols ? "stale" : "connecting", error: null }));
      socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        const token = authToken();
        if (token) {
          socket?.send(JSON.stringify({ type: "auth", token, session }));
        }
      };

      socket.onmessage = event => {
        const msg = parseRealtimeMarketMessage(String(event.data));
        if (!msg) return;
        setState({
          status: "live",
          symbols: msg.symbols,
          lastMessageAt: Date.now(),
          error: null,
        });
      };

      socket.onerror = () => {
        setState(prev => ({ ...prev, status: "error", error: "Realtime WebSocket error" }));
      };

      socket.onclose = () => {
        if (closed) return;
        setState(prev => ({ ...prev, status: prev.symbols ? "stale" : "connecting" }));
        reconnectTimer = setTimeout(connect, 2_000);
      };
    };

    staleTimer = setInterval(() => {
      setState(prev => {
        if (!prev.lastMessageAt || prev.status !== "live") return prev;
        return Date.now() - prev.lastMessageAt > STALE_AFTER_MS ? { ...prev, status: "stale" } : prev;
      });
    }, 1_000);

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (staleTimer) clearInterval(staleTimer);
      socket?.close();
    };
  }, [session, wsUrl]);

  return state;
}
